import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../Utils/useSEO";
import { motion } from "framer-motion";
import { ScrollReveal } from "../components/ScrollReveal";
import { Shield, Lock, FileText, Globe, Eye, UserCheck, HelpCircle } from "lucide-react";

export default function PrivacyPolicy() {
  useSEO({
    title: "Privacy Policy // Data Protection & Security Guidelines",
    description: "Learn how Supply Spot collects, uses, processes, and safeguards user and organizational data on our autonomous logistics platform."
  });

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 overflow-x-hidden font-sans pb-0 relative selection:bg-brand-orange/30 selection:text-text-primary">
      <Navbar />

      {/* Grid Overlay */}
      <div className="absolute inset-0 bg-grid-pattern-50 pointer-events-none -z-20" />

      {/* Glow Orbs */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-brand-orange/10 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-brand-amber/10 rounded-full blur-[140px] -z-10 pointer-events-none" />

      {/* HERO SECTION */}
      <section className="relative pt-36 pb-16 px-6 max-w-7xl mx-auto text-center flex flex-col items-center gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-semibold uppercase tracking-wider shadow-md"
        >
          <Shield className="w-3.5 h-3.5" />
          <span>LEGAL CENTER</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl font-bold tracking-tight text-text-primary font-title max-w-4xl"
        >
          Privacy Policy
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-text-secondary text-xs sm:text-sm max-w-2xl leading-relaxed"
        >
          Effective Date: June 20, 2025 &nbsp;|&nbsp; Last Updated: January 05, 2026
        </motion.p>
      </section>

      {/* CONTENT SECTION */}
      <section className="px-6 pb-24 max-w-7xl mx-auto relative z-10">
        <ScrollReveal duration={0.8} y={40}>
          <div className="glass-panel p-8 sm:p-12 md:p-16 rounded-3xl border border-brand-orange/10 shadow-2xl flex flex-col gap-10 text-left">
            
            {/* Overview / Introduction */}
            <div className="flex flex-col gap-4 border-b border-border-theme pb-8">
              <h2 className="text-xl font-title font-bold text-text-primary flex items-center gap-2">
                <FileText className="w-5 h-5 text-brand-orange" />
                Privacy Policy for Supply Spot Pvt Ltd
              </h2>
              <p className="text-text-secondary text-sm leading-relaxed">
                At Supply Spot Pvt Ltd (&ldquo;Supply Spot,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our enterprise software solutions, including our autonomous logistics command center, procurement matching suite, and supplier onboarding platform (collectively, the &ldquo;Services&rdquo;).
              </p>
              <p className="text-text-secondary text-sm leading-relaxed">
                By accessing or using the Services, you confirm that you have reviewed this Privacy Policy and understand how Supply Spot processes information in connection with the Services.
              </p>
            </div>

            {/* Section I */}
            <div className="flex flex-col gap-4">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <Eye className="w-5 h-5 text-brand-amber" />
                I. Information We Collect
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                We collect information primarily from your organization and through your use of the Services.
              </p>
              
              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">1. Personal and Account Data</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li><strong>Identity and Contact Data:</strong> Name, business email address, phone number, job role, and login credentials (stored in hashed form).</li>
                    <li><strong>Billing and Payment Data:</strong> Billing address and payment-related information. Payment processing is handled through secure, PCI-compliant third-party gateways (such as Razorpay, PayPal, or Stripe). Supply Spot does not store full credit or debit card numbers.</li>
                    <li><strong>User Activity Data:</strong> Information used for authentication, system security, audit logs, and customer support.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">2. Business Content Data (Data Processed on Your Behalf)</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li><strong>Customer Content:</strong> Data uploaded, stored, or generated by your organization&rsquo;s users while using the Services. This may include logistics logs, employee records, document uploads, invoices, images, customer contact data, device configurations, or other proprietary business information.</li>
                    <li><strong>Role as Data Processor:</strong> For Business Content Data, Supply Spot acts as a Data Processor and processes such data strictly in accordance with your organization&rsquo;s instructions. Your organization remains the Data Controller. Where required by law (e.g., GDPR), this relationship is governed by a separate Data Processing Addendum (DPA).</li>
                    <li>This includes employee attendance records, punch-in/punch-out logs, associated photographs (if enabled), work schedules, leave data, and organizational records generated while using the Services.</li>
                    <li>Supply Spot acts as a Data Processor, processing such data strictly based on the organization&rsquo;s instructions. The organization remains the Data Controller.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">3. Technical and Usage Data</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li><strong>Technical Identifiers:</strong> IP address, browser type, device identifiers, operating system, and application version.</li>
                    <li><strong>Usage and Analytics Data:</strong> Logs related to features accessed, time spent, pages viewed, and error reporting, collected to monitor performance and improve the Services.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section II */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <Lock className="w-5 h-5 text-brand-gold" />
                II. Camera Access and Image Capturing
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                Our application may request access to the device&apos;s camera solely to support photo-based verification, document uploads, punch-in/punch-out functionality, as well as other user-initiated features.
              </p>
              <ul className="list-disc pl-5 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                <li>Camera access is enabled only when the user explicitly initiates an action that requires capturing an image (e.g., document uploading or identity check).</li>
                <li>The application does not access the camera in the background or without user knowledge.</li>
                <li>Photographs captured during attendance marking or document uploads are used only for identity verification, validation, and verification purposes.</li>
                <li>Captured images are treated as Business Content Data and are accessible only to authorized organizational administrators.</li>
                <li>Supply Spot does not sell, share, or use captured images for advertising, profiling, or tracking purposes.</li>
              </ul>
            </div>

            {/* Section III */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <Globe className="w-5 h-5 text-brand-orange" />
                III. Location Access and Usage
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                The application may collect limited location information only where necessary to support specific features.
              </p>
              <ul className="list-disc pl-5 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                <li>Approximate location data (such as city, state, or country) may be derived from an IP address for purposes including security monitoring, fraud prevention, compliance, analytics, and service optimization.</li>
                <li>Precise (GPS-level) location data is collected only if explicitly required for a specific feature (such as cargo transit tracking or regional verification) and only after obtaining user consent.</li>
                <li>Location access, where applicable, is used only while the relevant feature is actively in use and not in the background.</li>
                <li>Users may choose not to grant location access; however, certain features may be limited as a result.</li>
                <li>Supply Spot does not sell, rent, or share location data for advertising or marketing purposes.</li>
              </ul>
            </div>

            {/* Section IV */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-brand-amber" />
                IV. Why We Collect and Use Your Information (Purpose of Processing)
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                We collect and use Personal Data and Business Content Data for the following purposes:
              </p>

              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">4. Processing Necessary for Contract</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li>To provide, operate, maintain, and secure the Services, including procurement matching, logistics tracking, and daily enterprise operations.</li>
                    <li>To record cargo transit status and route mapping, including validation through location-enabled features, where such features are enabled by the organization.</li>
                    <li>To manage transactions, RFQs, invoices, compliance checks, and daily supply chain activities.</li>
                    <li>To manage subscriptions, billing, service communications, and system notifications.</li>
                    <li>To provide technical support and respond to inquiries from users or administrators.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">5. Legitimate Business Interests</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li><strong>Service Improvement:</strong> To analyze usage data and improve functionality, performance, and user experience.</li>
                    <li><strong>Security and Integrity:</strong> To ensure security, accuracy, and compliance in logistics processes, and to detect, prevent, and respond to fraud, misuse, or security threats.</li>
                    <li><strong>Access Control:</strong> To provide secure, role-based access for employees, managers, and supply chain teams.</li>
                    <li><strong>Communications:</strong> To send service-related updates and product information. Marketing communications, where applicable, include opt-out options.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">6. Compliance with Law</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    To comply with applicable legal obligations, regulatory requirements, audits, or lawful requests from government or regulatory authorities.
                  </p>
                </div>
              </div>
            </div>

            {/* Section V */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <Lock className="w-5 h-5 text-brand-gold" />
                V. Data Sharing, Storage, and Security
              </h3>
              
              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">7. Data Sharing and Disclosure</h4>
                  <ul className="list-disc pl-5 mt-1 text-text-secondary text-xs flex flex-col gap-1.5 leading-relaxed">
                    <li><strong>No Sale of Data:</strong> Supply Spot does not sell or rent Personal Data or Business Content Data.</li>
                    <li><strong>Service Providers:</strong> Data may be shared with trusted third-party service providers (such as cloud hosting providers, payment processors, and analytics services) under strict confidentiality and security obligations.</li>
                    <li><strong>Legal Requirements:</strong> Data may be disclosed if required by law, regulation, or valid legal process.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">8. Data Storage and International Transfers</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    Data is primarily stored on secure cloud servers located in India. For redundancy, performance, or business continuity, data may be processed in other jurisdictions (such as the United States or Singapore), subject to appropriate legal safeguards, including Standard Contractual Clauses where applicable.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">9. Security Measures</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    We implement industry-standard security controls, including encryption in transit and at rest, access controls, firewalls, and continuous monitoring. While we take strong measures to protect data, no system can guarantee absolute security.
                  </p>
                </div>
              </div>
            </div>

            {/* Section VI */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <Shield className="w-5 h-5 text-brand-orange" />
                VI. Cookies, Tracking, and User Rights
              </h3>
              
              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">10. Cookies and Tracking Technologies</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    We use first-party and third-party cookies and similar technologies for session management, performance optimization, and analytics. By using the Services, you consent to such use as described.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">11. Your Data Rights</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    Depending on your jurisdiction, you may have rights to access, correct, delete, restrict, or object to the processing of your Personal Data. Requests can be submitted to <a href="mailto:support@supplyspot.ai" className="text-brand-orange hover:underline">support@supplyspot.ai</a>, and we will respond within legally required timelines.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">12. Data Retention</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    We retain Personal Data and Business Content Data only for as long as necessary to provide the Services, comply with legal obligations, resolve disputes, or enforce agreements. Data is securely deleted or anonymized thereafter.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">13. Children&rsquo;s Privacy</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    The Services are intended for business users aged 18 and above. We do not knowingly collect personal information from individuals under 18.
                  </p>
                </div>
              </div>
            </div>

            {/* Section VII */}
            <div className="flex flex-col gap-4 border-t border-border-theme/40 pt-8">
              <h3 className="text-lg font-title font-bold text-text-primary flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-brand-amber" />
                VII. Policy Updates and Contact
              </h3>
              
              <div className="flex flex-col gap-4 pl-4 mt-2">
                <div>
                  <h4 className="font-bold text-sm text-text-primary">14. Policy Updates</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed">
                    We may update this Privacy Policy from time to time. The &ldquo;Last Updated&rdquo; date will be revised accordingly, and material changes will be communicated through the Services or by email.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-sm text-text-primary">15. Contact Information</h4>
                  <p className="text-text-secondary text-xs mt-1 leading-relaxed mb-4">
                    If you have any questions or concerns regarding this Privacy Policy or our data practices, please contact:
                  </p>
                  
                  <div className="p-5 rounded-2xl bg-bg-secondary/40 border border-brand-orange/15 text-xs text-text-secondary flex flex-col gap-2">
                    <span className="font-bold text-text-primary text-sm">Supply Spot Pvt Ltd</span>
                    <span><strong>Attention:</strong> Legal / Privacy Office</span>
                    <span><strong>HQ Address:</strong> N8, Souvenir, Kalapatti Road, Coimbatore - 641014, Tamil Nadu, India</span>
                    <span><strong>Email:</strong> <a href="mailto:support@supplyspot.ai" className="text-brand-orange hover:underline">support@supplyspot.ai</a></span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </ScrollReveal>
      </section>

      <Footer />
    </div>
  );
}
